import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux';
import { PlayerTransaction, PlayerTransactionsResponse, Product } from '../../http/protocol';
import { usePrevious } from '../../user/ui/utils';
import { getCurrencyDefinitions } from '../../selectors/settings-selectors';
import { getActiveCurrency, getLoggedIn } from '../../selectors/user-selectors';
import { Status } from '../../journey/UserJourneyReducer';
import { getContainer } from '../../ioc/IOCSetup';
import { t } from '../../i18n';
import { Header, Table, TableRow } from '../../core/ui/organisms/Table';
import { getAmountClass, getCasinoType, getPaymentType } from '../TransactionUtils';
import moment from 'moment';
import { Big } from "big.js";
import { MemoizedTransactionAmountComponent, MemoizedTransactionTypeComponent, TableStatusAndPaginations, TransactionAmount, TransactionReference, TransactionRefernceComponent, TransactionTypes } from './TransactionRowComponents';
import { Redirect } from 'react-router';



export interface FilteredTransaction {
    transactionType?: string;
    amount?: number;
    currencyCode?: string;
    fractionalDigits?: number;
    providerMaskedName?: string;
    timestamp?: Date;
    reference?: any;
}



type TransactionToViewModel = {
    type?: TransactionTypes;
    reference?: TransactionReference;
    amount?: TransactionAmount;
}

export default function OtherTransactions() {

    const userLoggedIn = useSelector(getLoggedIn);
    const activeCurrency = useSelector(getActiveCurrency);
    const previousCurrency = usePrevious(activeCurrency);
    const currencyDefinitions = useSelector(getCurrencyDefinitions)

    const [asyncStatus, setAsyncStatus] = useState<Status>(Status.NONE)
    const [pagination, setPagination] = useState({ offset: 0, limit: 20 });

    const [transactions, setTransactions] = useState<{ items: FilteredTransaction[] | undefined }>(
        {
            items: undefined,
        }
    )

    const handlePagination = () => {
        setPagination((prev) => ({ ...prev, offset: prev.offset + prev.limit }));
    };

    const findCurrencyDefinition = (code: string) => currencyDefinitions.find((c) => c.code == code)
    const formatByFractionalDigits = (amount: any, code: string) => {
        let fractionalDigits = findCurrencyDefinition(code)?.fractionalDigits;
        return new Big(amount).toFixed(fractionalDigits)
    }

    const filterTransactions = (transactions: PlayerTransaction[]): FilteredTransaction[] => {
        return transactions.filter((t) => {
            return t.attributes?.PAYMENT_TYPE !== "DEPOSIT" && t.attributes?.PAYMENT_TYPE !== "WITHDRAWAL"
        }).map(({ amount, attributes, id, timestamp }) => ({
            transactionType: getPaymentType(attributes?.["PAYMENT_TYPE"], attributes),
            amount: amount?.amount,
            currencyCode: amount?.currencyCode,
            fractionalDigits: amount?.fractionalDigits,
            timestamp,
            reference: id
        }));
    };

    const getTransactions = async () => {
        setAsyncStatus(Status.LOADING);
        try {

            const transactions = await new Promise<PlayerTransactionsResponse>((resolve, reject) => {
                getContainer()
                    .getTransactionService()
                    .listTransactions({ ...pagination, currency: activeCurrency?.code, type: Product.PAYMENT })
                    .then(resolve)
                    .catch(reject);
            })

            if (pagination.offset === 0) {
                setTransactions({
                    items: filterTransactions(transactions.transactions ?? []),

                });
            } else {
                setTransactions((p) => ({
                    items: [...p.items ?? [], ...filterTransactions(transactions.transactions ?? [])]
                }));
            }
            setAsyncStatus(Status.DONE);
        } catch (err) {
            setAsyncStatus(Status.ERROR);
        }
    };

    const resetTransactions = () => {
        setPagination({ offset: 0, limit: 20 });
        setTransactions({ items: undefined });
    }


    useEffect(() => {
        if (transactions.items == undefined || activeCurrency !== previousCurrency)
            getTransactions();
        return () => {
            resetTransactions()
        }
    }, [activeCurrency])

    useEffect(() => {
        if (pagination.offset !== 0) {
            getTransactions();
        }
    }, [pagination]);


    const hasTransactions = transactions.items !== undefined && transactions.items?.length > 0;


    const mapTransactionToViewModel = useCallback((transaction: FilteredTransaction): TransactionToViewModel => {
        const formattedTimeStamp = moment(transaction.timestamp).format("MMM DD, YYYY, h:mm A");
        const formattedAmount = formatByFractionalDigits(transaction.amount, transaction.currencyCode ?? "eur");
        const isPositveAmountClass = getAmountClass(Number(formattedAmount))
        return {
            type: { product: transaction.transactionType, timeStamp: formattedTimeStamp, class: isPositveAmountClass },
            reference: { reference: transaction.reference },
            amount: { amount: formatByFractionalDigits(transaction.amount, transaction.currencyCode ?? "eur"), currency: transaction.currencyCode }
        }
    }, [getCasinoType])

    const transactionColumnLabels: Record<string, Header> = useMemo(() => ({
        'type': { name: t('transactions.transaction_wallet_table.type') },
        'reference': { name: t('transactions.transaction_wallet_table.reference') },
        'amount': { name: t('transactions.transaction_wallet_table.amount') },
    }), [t]);

    const columnComponents = useMemo(() => ({
        'type': (key: string, value: any) => <MemoizedTransactionTypeComponent key={key} value={value} identifier={key} />,
        'reference': (key: string, value: any) => <TransactionRefernceComponent key={key} value={value} identifier={key} />,
        'amount': (key: string, value: any) => <MemoizedTransactionAmountComponent key={key} value={value} identifier={key} />,
    }), []);


    if (!userLoggedIn) {
        return <Redirect to={"/"} />
    }

    return (
        <div>
            <Table headers={transactionColumnLabels} className='generic-table other-transaction-table'>
                {transactions.items?.map((data, idx) => {
                    return (
                        <TableRow
                            key={idx}
                            columnComponents={columnComponents}
                            columnData={mapTransactionToViewModel(data)}
                            className="other-transaction"
                        />
                    )
                })}
                <TableStatusAndPaginations status={asyncStatus} handlePagination={handlePagination} hasTransactions={hasTransactions} activeCurrency={{ name: activeCurrency?.name, code: activeCurrency?.code }} />

            </Table>
        </div>
    )
}




